import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  useCreateBlockNote,
  FormattingToolbar,
  FormattingToolbarController,
  getFormattingToolbarItems,
  useComponentsContext,
  useBlockNoteEditor,
  useEditorSelectionChange,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/core/style.css';
import '@blocknote/mantine/style.css';
import { saveJournal } from '../../actions/planningAction';
import './PlanningJournal.css';

const AUTOSAVE_DELAY = 1500;

function plainWordCount(md) {
  if (!md) return 0;
  const plain = md.replace(/[#*_`>\[\]!()-]/g, ' ').replace(/\s+/g, ' ').trim();
  return plain ? plain.split(' ').length : 0;
}

// Reuse same remove-link button pattern as doc editor
function RemoveLinkButton() {
  const editor = useBlockNoteEditor();
  const Components = useComponentsContext();
  const [hasLink, setHasLink] = useState(false);
  useEditorSelectionChange(() => { setHasLink(!!editor.getSelectedLinkUrl()); }, editor);
  if (!hasLink) return null;
  return (
    <Components.FormattingToolbar.Button
      mainTooltip="Remove link"
      label="Unlink"
      onClick={() => {
        editor.transact((tr) => {
          const linkType = editor.pmSchema.marks.link;
          if (!linkType) return;
          const { from, to } = tr.selection;
          tr.removeMark(from, to, linkType);
        });
        setHasLink(false);
      }}
    />
  );
}

export default function PlanningJournal({ vision, visionId, theme }) {
  const dispatch = useDispatch();
  const [saveStatus, setSaveStatus] = useState('saved');
  const [wordCount,  setWordCount]  = useState(0);
  const timerRef    = useRef(null);
  const lastSavedRef = useRef(vision?.journal || '');
  const loadedRef   = useRef(false);

  const editor = useCreateBlockNote();

  // Seed editor from stored markdown on mount
  useEffect(() => {
    const md = vision?.journal || '';
    const init = async () => {
      if (md.trim()) {
        const blocks = await editor.tryParseMarkdownToBlocks(md);
        editor.replaceBlocks(editor.document, blocks);
        setWordCount(plainWordCount(md));
      }
      loadedRef.current = true;
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getMd = useCallback(async () => {
    try { return await editor.blocksToMarkdownLossy(editor.document); } catch (_) { return ''; }
  }, [editor]);

  const save = useCallback(async () => {
    const md = await getMd();
    if (md === lastSavedRef.current) return;
    setSaveStatus('saving');
    try {
      await dispatch(saveJournal(visionId, md));
      lastSavedRef.current = md;
      setSaveStatus('saved');
    } catch (_) {
      setSaveStatus('unsaved');
    }
  }, [dispatch, getMd, visionId]);

  const queueSave = useCallback(() => {
    setSaveStatus('unsaved');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(save, AUTOSAVE_DELAY);
  }, [save]);

  const onEditorChange = useCallback(async () => {
    if (!loadedRef.current) return;
    const md = await getMd();
    setWordCount(plainWordCount(md));
    queueSave();
  }, [getMd, queueSave]);

  // Flush on unmount
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    getMd().then(md => {
      if (md !== lastSavedRef.current) {
        dispatch(saveJournal(visionId, md)).catch(() => {});
      }
    });
  }, [dispatch, getMd, visionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pj-wrap">
      <div className="pj-header">
        <div className="pj-header-left">
          <span className="pj-eyebrow">Journal</span>
          <span className="pj-vision-name">{vision?.title}</span>
        </div>
        <div className="pj-header-right">
          <span className={`pj-save-status pj-save-status--${saveStatus}`}>
            {saveStatus === 'saving'  && '● Saving…'}
            {saveStatus === 'saved'   && '● Saved'}
            {saveStatus === 'unsaved' && '○ Unsaved'}
          </span>
          <span className="pj-word-count">{wordCount.toLocaleString()} word{wordCount !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="pj-page-wrap">
        <p className="pj-doc-tagline">A journal of how you are making your vision a reality, for future you to see.</p>
        <div className="pj-document">
          <BlockNoteView
            editor={editor}
            theme={theme === 'light' ? 'light' : 'dark'}
            onChange={onEditorChange}
            formattingToolbar={false}
          >
            <FormattingToolbarController
              formattingToolbar={() => (
                <FormattingToolbar>
                  {getFormattingToolbarItems()}
                  <RemoveLinkButton key="removeLink" />
                </FormattingToolbar>
              )}
            />
          </BlockNoteView>
        </div>
      </div>
    </div>
  );
}
